/**
 * `useCoverageReview` — TanStack Query hook for the S2 grade & skill
 * coverage review (`GET /api/cycles/{id}/coverage-review`).
 *
 * Lead-only. Answers the question the roster lead asks before releasing a
 * week: is every ward covered by the grades the department requires, every
 * session, and where not, why? The payload carries one cell per
 * (date, station, session) with the people seated and their tiers, the
 * per-grade bands under it, and a one-line `reason` on a short cell —
 * plus the pooled group floors that span several stations.
 *
 * Consumed by `<CoverageSection/>` inside `<CoverageModal/>`, opened from
 * the cycle workspace's "Coverage" chip.
 */
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { type ApiError, apiFetch } from "./client";
import { liveRefetchOptions } from "./liveRefetch";
import type { components } from "./schema.gen";

export type CoverageReviewResponse = components["schemas"]["CoverageReviewResponse"];
export type AbsentPerson = components["schemas"]["AbsentPerson"];
export type CoverageCell = components["schemas"]["CoverageCell"];
export type CoverageStation = components["schemas"]["CoverageStation"];
export type FloorReview = components["schemas"]["FloorReview"];
export type BandReview = components["schemas"]["BandReview"];
export type SeatedPerson = components["schemas"]["SeatedPerson"];

const STALE_TIME_MS = 30_000;

export function coverageReviewQueryKey(
  cycleId: string,
  accountId: string | null,
): readonly unknown[] {
  return ["cycle-coverage-review", cycleId, accountId ?? ""];
}

/**
 * Invalidation filter matching the cached coverage review of one cycle
 * under whichever account it was read.
 *
 * Backlog #91 (checklist 05.20). The "Coverage · N short" chip and the
 * coverage side panel are the SAME query, and the writes that change what
 * is short (report off sick, a ladder rung, a withdrawn MC) live in hooks
 * with no `accountId` to hand `coverageReviewQueryKey`. Without this the
 * chip kept saying "2 short" while the grid said −4.
 */
export function coverageReviewQueryFilter(cycleId: string): {
  predicate: (query: { queryKey: readonly unknown[] }) => boolean;
} {
  return {
    predicate: (query) =>
      query.queryKey[0] === "cycle-coverage-review" && query.queryKey[1] === cycleId,
  };
}

interface UseCoverageReviewOpts {
  /** Force-disable the fetch (the modal only fetches while it is open). */
  enabled?: boolean;
}

export function useCoverageReview(cycleId: string, opts: UseCoverageReviewOpts = {}) {
  const { accountId } = useAuth();
  const enabledByCaller = opts.enabled ?? true;
  return useQuery<CoverageReviewResponse, ApiError | Error>({
    queryKey: coverageReviewQueryKey(cycleId, accountId),
    enabled: Boolean(cycleId && accountId) && enabledByCaller,
    staleTime: STALE_TIME_MS,
    retry: false,
    // #90 — the "Coverage · N short" chip is a live count; an absence
    // reported elsewhere has to move it. See `liveRefetch.ts`.
    ...liveRefetchOptions,
    queryFn: () => apiFetch<CoverageReviewResponse>(`/api/cycles/${cycleId}/coverage-review`),
  });
}
